import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, DataTableError, DataTablePaginator, useLocalDataTableData } from '@utmost.co/utmost-design-system';
import { useDeepMemo, DefaultButton, Drawer } from '@beeline.com/bp-design-system';
import styled from 'styled-components';
import { useCustomObjectValuesIntl } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/translations/useCustomObjectValuesIntl';
import { useHistory, useLocation } from 'react-router';
import {
  CustomObject,
  CustomObjectsQuery
} from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/CustomObject.types';
import { useCustomObjectValuesColumns } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/useCustomObjectValuesColumns';
import { useTypedMutation } from 'utils/hooks/useTypedMutation';
import { UpdatableStatusModel } from 'src/graphql/types';
import { DeleteCustomObjectDocument } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/graphql/__generated__/DeleteCustomObject.graphql';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';
import { GetCustomObjectsDocument } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/graphql/__generated__/GetCustomObjects.graphql';
import { filterNotEmpty } from 'utils/data/listHelpers';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { useDataTableFiltersEvent } from 'integrations/mixpanel/hooks/useDataTableFiltersEvent';
import { CustomObjectValuesToolbar } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/CustomObjectValuesToolbar';
import { CreateEditCustomObjectValueDialog } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/CreateEditCustomObjectValueDialog';
import { screenSizeQuery } from '@utmost.co/utmost-design-system/src/utils/styledComponents';
import { breakpoints } from 'src/utils/BreakPoints';
import { PageTitle } from 'common/header/PageTitle';
import DefaultPageView from 'common/pageViews/DefaultPageView';
import { convertCustomObjectToDataTableObject } from '@oc/pages/settings/CustomFieldsAndObjects/Objects/CustomObjectValues/convertCustomObjectToDataTableObject';

interface Props {
  customObjectDefinitionUid: string | undefined;
  customObjectDefinitionName: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export const CustomObjectValuesSideBar: FC<React.PropsWithChildren<Props>> = ({
  customObjectDefinitionUid,
  customObjectDefinitionName,
  isOpen,
  onClose
}) => {
  const { get: getCustomObjects, data, loading, error, refetch } = useTypedQuery(GetCustomObjectsDocument);
  const [variables, setVariables] = useState<CustomObjectsQuery>();

  useEffect(() => {
    if (variables?.page?.limit && customObjectDefinitionUid) {
      getCustomObjects({
        variables: {
          customObjectDefinitionUid: customObjectDefinitionUid,
          statuses: [UpdatableStatusModel.Active, UpdatableStatusModel.Inactive],
          page: {
            limit: variables?.page?.limit,
            offset: variables?.page?.offset
          }
        }
      });
    }
  }, [customObjectDefinitionUid, getCustomObjects, variables]);

  const customObjects: CustomObject[] = useMemo(
    () => filterNotEmpty(data?.customObjects?.items) ?? [],
    [data?.customObjects?.items]
  );
  const dataTableCustomObjects = useDeepMemo(
    () => convertCustomObjectToDataTableObject(customObjects),
    [customObjects]
  );
  const { searchValue, setLocalSearch, localPagination } = useLocalDataTableData({
    networkData: dataTableCustomObjects,
    setLocalPagination: true,
    setSearch: { localSearchKeys: ['name', 'uid', 'referenceId'] }
  });
  localPagination.totalCount = data?.customObjects.totalCount ?? 0;
  if (variables?.page?.limit) {
    localPagination.pageCount = Math.ceil(localPagination.totalCount / variables.page.limit);
    if (variables?.page?.offset) {
      localPagination.pageIndex = variables.page.offset / variables.page.limit;
    }
  }

  const [customObjectToModify, setCustomObjectToModify] = useState<CustomObject>();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const { set: deleteCustomObject } = useTypedMutation(DeleteCustomObjectDocument, {});

  const intl = useCustomObjectValuesIntl();

  const fullScreen = useState(false);

  const onDeleteActionHandler = useCallback(
    (customObject: CustomObject) => {
      if (customObject.uid) {
        deleteCustomObject({ variables: { uid: customObject.uid } });
      }
      refetch();
      setOpenEditModal(false);
    },
    [deleteCustomObject, refetch]
  );

  const onEditActionHandler = useCallback((customObject: CustomObject) => {
    setCustomObjectToModify(customObject);
    setOpenEditModal(true);
  }, []);

  const onCloseHandler = useCallback(() => {
    setOpenEditModal(false);
    setCustomObjectToModify(undefined);
    onClose();
  }, [onClose]);

  const onEditCustomObjectValueHandler = useCallback(() => {
    setOpenEditModal(false);
    setCustomObjectToModify(undefined);
    refetch();
  }, [refetch]);

  const onCloseEditCustomObjectValueHandler = useCallback(() => {
    setOpenEditModal(false);
  }, []);

  const columns = useCustomObjectValuesColumns({
    onEdit: onEditActionHandler,
    onDelete: onDeleteActionHandler
  });

  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations(
    'SETTINGS_CUSTOM_OBJECT_VALUES'
  );
  useDataTableFiltersEvent<DeepPartial<CustomObjectsQuery>>(variables, 'SETTINGS_CUSTOM_OBJECT_VALUES');

  return (
    <>
      <Drawer anchor="right" open={isOpen}>
        <DefaultPageView
          pageTitle={<PageTitle title={intl.customObjectValuesTitle({ name: customObjectDefinitionName })} />}
        >
          <StyledWrapper data-test="custom-object-values-editor" style={{ width: fullScreen[0] ? '100vw' : undefined }}>
            <DataTable
              pageCount={localPagination.pageCount}
              data={localPagination.paginatedData ?? []}
              onColumnConfigurationChanged={patchColumnConfiguration}
              initialState={columnConfiguration}
              onVariablesChanged={setVariables}
              totalCount={localPagination.totalCount}
              isFetching={loading}
              columns={columns}
            >
              <DataTableError
                error={!customObjects?.length && error?.message}
                useHistory={useHistory}
                useLocation={useLocation}
              />
              <CustomObjectValuesToolbar
                onSubmitDone={refetch}
                pageDataCount={(localPagination.paginatedData ?? []).length}
                totalCount={localPagination.totalCount}
                customObjectDefinitionUid={customObjectDefinitionUid}
                onValueChange={setLocalSearch}
                value={searchValue ?? ''}
              />

              <DataTablePaginator
                fetchNextPage={(page) => localPagination.setPageIndex(page.pageIndex)}
                pageIndex={localPagination.pageIndex}
              />
            </DataTable>
          </StyledWrapper>
        </DefaultPageView>
        <StyledFooter>
          <DefaultButton.Contained dataTest="close-custom-object-values-btn" onClick={onCloseHandler}>
            {intl.close}
          </DefaultButton.Contained>
        </StyledFooter>
      </Drawer>
      <CreateEditCustomObjectValueDialog
        onCloseModal={onCloseEditCustomObjectValueHandler}
        onCreate={onEditCustomObjectValueHandler}
        onEdit={onEditCustomObjectValueHandler}
        isOpen={openEditModal}
        isEdit={true}
        model={customObjectToModify}
        customObjectDefinitionUid={customObjectDefinitionUid}
      />
    </>
  );
};

const StyledFooter = styled.div`
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
  box-shadow: 0px -2px 6px rgba(0, 0, 0, 0.1);
`;

const StyledWrapper = styled.div`
  transition: width 0.2s ease-in-out;
  height: 100%;
  display: flex;
  flex-direction: column;

  .content {
    overflow: auto;
    height: 100%;
  }

  ${screenSizeQuery.xs`
            width: 100vw;
        `}
  ${screenSizeQuery.sm`
            width: ${breakpoints.sm}px
        `}
  ${screenSizeQuery.md`
            width: ${breakpoints.md}px
        `}
  ${screenSizeQuery.lg`
            width: ${breakpoints.lg}px
        `}
  ${screenSizeQuery.xl`
            width: ${breakpoints.xl}px;
        `}
  &.compact {
    width: 400px;
  }
`;
